import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, GraduationCap, Briefcase, Award, Globe, MapPin } from 'lucide-react';
import type { PassportStation, StationType } from '../../api/passport';
import { isPresent, sortStationsByRecency } from './stationOrder';
import { humanizeTag } from './format';

interface PassportCoverProps {
  /** First (anonymized) CV page image (base64, no data-uri prefix) → small preview. */
  thumbnail?: string | null;
  /** Render a faux "CV document" placeholder in the thumbnail slot (teaser previews). */
  cvPlaceholder?: boolean;
  realTalkSentence?: string | null;
  tags?: string[] | null;
  /** Career/education stations (chronological, earliest first). */
  stations?: PassportStation[] | null;
  /** Passport/meta still loading → show skeletons for missing pieces. */
  loading?: boolean;
  /** @deprecated no longer shown in UI; kept for caller compatibility. */
  score?: number;
  onClick: () => void;
  /** Optional content rendered in the footer line, right of "stops"
   *  (replaces the default "View passport" CTA). e.g. a reaction bar. */
  footerRight?: React.ReactNode;
  /** Hide the dark "Passport" header band (e.g. condensed inbox rows). */
  hideHeader?: boolean;
  /** Hide the green "Now" badge on present stations (e.g. inbox rows). */
  hideNowBadge?: boolean;
  /** Replace the entire default footer (stops + CTA) with custom content,
   *  e.g. a chat preview in the messaging inbox. */
  footer?: React.ReactNode;
  className?: string;
}

const TYPE_ICON: Record<StationType, React.ComponentType<{ className?: string }>> = {
  education: GraduationCap,
  work: Briefcase,
  other: Award,
};

const COVER_COUNT = 3;

// Anonymous, passport-styled teaser card for the Explore feed. No name / photo /
// current role — a passport header band, a Real Talk hook, the most recent
// stations (present first) and a few tags. Tapping opens the full Passport.
const PassportCover: React.FC<PassportCoverProps> = ({
  thumbnail,
  cvPlaceholder = false,
  realTalkSentence,
  tags,
  stations,
  loading = false,
  onClick,
  footerRight,
  hideHeader = false,
  hideNowBadge = false,
  footer,
  className = '',
}) => {
  const shownTags = (tags || []).slice(0, 4);

  const allStations = (stations || []).filter((s) => s.org || s.title);
  // Most recent first (present on top), independent of upstream order.
  const journey = sortStationsByRecency(allStations).slice(0, COVER_COUNT);
  const moreCount = Math.max(0, allStations.length - COVER_COUNT);

  // Distinct places, preferring city granularity (falls back to country).
  const placeCount = new Set(
    allStations.map((s) => (s.city || s.country || '').trim().toLowerCase()).filter(Boolean),
  ).size;

  return (
    <motion.div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.99 }}
      className={`group w-full text-left cursor-pointer bg-white rounded-2xl border border-slate-200 hover:border-pink-300 hover:shadow-lg hover:shadow-pink-100/40 transition-all overflow-hidden flex flex-col ${className}`}
    >
      {/* Passport header band (slim, dark — pink as accent) */}
      {!hideHeader && (
      <div className="relative px-3.5 py-2 bg-slate-900 text-white flex items-center justify-between overflow-hidden">
        <div className="absolute -right-3 -top-5 w-16 h-16 rounded-full bg-white/5 pointer-events-none" />
        <div className="relative flex items-center gap-1.5">
          <Globe className="w-3 h-3 text-pink-400" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-semibold">Passport</span>
        </div>
        <div className="relative flex items-center gap-1.5">
          {placeCount > 0 && (
            <span className="flex items-center gap-1 text-[10px] font-semibold bg-white/10 ring-1 ring-white/15 rounded-full px-2 py-0.5">
              <MapPin className="w-2.5 h-2.5" />
              {placeCount}
            </span>
          )}
        </div>
      </div>
      )}

      {/* Body */}
      <div className="p-4 flex flex-col gap-3.5">
        {realTalkSentence ? (
          <p className="text-[13px] text-slate-700 font-medium italic leading-snug line-clamp-2">
            “{realTalkSentence}”
          </p>
        ) : null}

        {(journey.length > 0 || thumbnail || cvPlaceholder || loading) && (
          <div className="flex items-start gap-3">
            {thumbnail ? (
              <div className="w-16 flex-shrink-0">
                <div className="relative aspect-[3/4] rounded-lg border border-slate-200 bg-slate-50 overflow-hidden shadow-sm group-hover:border-pink-200 transition-colors">
                  <img
                    src={`data:image/png;base64,${thumbnail}`}
                    alt="CV preview"
                    className="w-full h-full object-cover object-top opacity-90"
                  />
                  <div className="absolute bottom-0 inset-x-0 bg-slate-900/55 text-white text-[8px] font-bold tracking-wide text-center py-px">
                    CV
                  </div>
                </div>
              </div>
            ) : cvPlaceholder ? (
              <div className="w-16 flex-shrink-0">
                <div className="relative aspect-[3/4] rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm group-hover:border-pink-200 transition-colors">
                  {/* Faux redacted CV document */}
                  <div className="absolute inset-0 p-1.5 flex flex-col gap-[3px]">
                    <div className="h-1 w-3/4 rounded-full bg-slate-300" />
                    <div className="h-1 w-1/2 rounded-full bg-slate-200" />
                    <div className="mt-1 h-[3px] w-full rounded-full bg-slate-100" />
                    <div className="h-[3px] w-5/6 rounded-full bg-slate-100" />
                    <div className="h-[3px] w-full rounded-full bg-slate-100" />
                    <div className="mt-1 h-[3px] w-2/3 rounded-full bg-slate-100" />
                    <div className="h-[3px] w-full rounded-full bg-slate-100" />
                    <div className="h-[3px] w-4/5 rounded-full bg-slate-100" />
                  </div>
                  <div className="absolute bottom-0 inset-x-0 bg-slate-900/55 text-white text-[8px] font-bold tracking-wide text-center py-px">
                    CV
                  </div>
                </div>
              </div>
            ) : null}

            {journey.length > 0 ? (
              <div className="flex-1 min-w-0 space-y-2.5">
                {journey.map((s, i) => {
                  const Icon = TYPE_ICON[s.type] || Award;
                  const present = isPresent(s.year);
                  return (
                    <div key={i} className="flex items-start gap-2.5 min-w-0">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          present ? 'bg-emerald-50 text-emerald-600' : 'bg-pink-50 text-pink-500'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="block text-[13px] font-semibold text-slate-700 truncate">
                          {s.org || s.title}
                        </span>
                        {s.org && s.title && (
                          <div className="text-[11px] text-slate-400 truncate">{s.title}</div>
                        )}
                      </div>
                      {present && !hideNowBadge && (
                        <span className="text-[9px] font-bold uppercase tracking-wide text-emerald-600 bg-emerald-50 rounded px-1 py-px flex-shrink-0 mt-0.5">
                          Now
                        </span>
                      )}
                    </div>
                  );
                })}
                {moreCount > 0 && (
                  <div className="text-[11px] text-slate-400 pl-[34px]">
                    +{moreCount} more {moreCount === 1 ? 'stop' : 'stops'}
                  </div>
                )}
              </div>
            ) : loading ? (
              <div className="flex-1 min-w-0 space-y-2.5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-2.5" style={{ opacity: 1 - i * 0.18 }}>
                    <div className="w-6 h-6 rounded-full bg-pink-50 animate-pulse flex-shrink-0" />
                    <div className="h-3 flex-1 bg-slate-200/70 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )}

        {shownTags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {shownTags.map((tag) => (
              <span
                key={tag}
                className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-pink-50 text-pink-600 border border-pink-100"
              >
                {humanizeTag(tag)}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {/* Custom footer (e.g. inbox chat preview) replaces the default CTA line. */}
      {footer !== undefined ? (
        <div className="px-3.5 py-2.5 border-t border-slate-100">{footer}</div>
      ) : (
      /* Footer CTA */
      <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between gap-2">
        {loading && allStations.length === 0 ? (
          <span className="h-2.5 w-12 bg-slate-200/60 rounded animate-pulse" />
        ) : (
          <span className="text-[11px] text-slate-400 font-medium flex-shrink-0">
            {allStations.length} {allStations.length === 1 ? 'stop' : 'stops'}
          </span>
        )}
        {footerRight ? (
          <div className="flex items-center justify-end flex-wrap gap-1">{footerRight}</div>
        ) : (
          <span className="flex items-center gap-1 text-[12px] font-semibold text-pink-500 group-hover:gap-1.5 transition-all">
            View passport <ArrowRight className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
      )}
    </motion.div>
  );
};

export default PassportCover;
